/**
 * Utility functions for working with colors and theming
 */

/**
 * Validate and sanitize hex color to prevent CSS injection
 */
export function sanitizeHexColor(color: string | null | undefined): string | null {
  // Handle null/undefined gracefully
  if (!color) {
    return null;
  }

  // Strict validation: only allow 3 or 6 digit hex colors
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

  if (!hexRegex.test(color)) {
    console.warn('Invalid hex color format:', color);
    return null;
  }

  return color;
}

/**
 * Convert hex color to RGB values
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Handle short format (#fff -> #ffffff)
  const fullHex = cleanHex.length === 3
    ? cleanHex.split('').map(char => char + char).join('')
    : cleanHex;

  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert RGB to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Adjust color brightness for dark mode
 * Lightens colors by a percentage for better visibility on dark backgrounds
 */
export function adjustColorForDarkMode(hex: string, lightenPercent: number = 30): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const adjust = (value: number) => {
    const adjusted = value + ((255 - value) * lightenPercent) / 100;
    return Math.min(255, Math.round(adjusted));
  };

  return rgbToHex(adjust(rgb.r), adjust(rgb.g), adjust(rgb.b));
}

/**
 * Calculate contrast ratio between two colors
 * Used for accessibility (WCAG AA requires 4.5:1 for normal text)
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const getLuminance = (hex: string): number => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;

    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((val) => {
      const normalized = val / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determine if a color should use light or dark text for readability
 */
export function shouldUseLightText(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;

  // Using perceived brightness formula
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness < 128;
}

/**
 * Apply tenant colors to CSS custom properties
 */
export function applyTenantColors(primaryColor: string | null | undefined, secondaryColor: string | null | undefined, isDarkMode: boolean) {
  const root = document.documentElement;

  // Sanitize colors to prevent CSS injection
  const safePrimary = sanitizeHexColor(primaryColor);
  const safeSecondary = sanitizeHexColor(secondaryColor);

  if (!safePrimary || !safeSecondary) {
    // Fall back to default colors (this is expected for new tenants without custom colors)
    root.style.setProperty('--color-primary', isDarkMode ? '#60a5fa' : '#1e40af');
    root.style.setProperty('--color-secondary', isDarkMode ? '#14b8a6' : '#059669');
    root.style.setProperty('--color-accent', isDarkMode ? '#14b8a6' : '#059669');
    return;
  }

  if (isDarkMode) {
    // Lighten colors for dark mode
    root.style.setProperty('--color-primary', adjustColorForDarkMode(safePrimary));
    root.style.setProperty('--color-secondary', adjustColorForDarkMode(safeSecondary));
  } else {
    root.style.setProperty('--color-primary', safePrimary);
    root.style.setProperty('--color-secondary', safeSecondary);
  }

  // Set foreground colors based on contrast
  root.style.setProperty(
    '--color-primary-foreground',
    shouldUseLightText(safePrimary) ? '#ffffff' : '#0f172a'
  );
  root.style.setProperty(
    '--color-secondary-foreground',
    shouldUseLightText(safeSecondary) ? '#ffffff' : '#0f172a'
  );

  // Derive accent color (slightly shifted hue from primary)
  const accentColor = isDarkMode
    ? adjustColorForDarkMode(safePrimary, 40)
    : safePrimary;
  root.style.setProperty('--color-accent', accentColor);
  root.style.setProperty(
    '--color-accent-foreground',
    shouldUseLightText(accentColor) ? '#ffffff' : '#0f172a'
  );
}

/**
 * Get status color for service areas
 */
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    Planned: 'var(--color-status-planned)',
    UnderConstruction: 'var(--color-status-construction)',
    Available: 'var(--color-status-available)',
    Cancelled: 'var(--color-status-cancelled)',
  };
  return colors[status] || '#6b7280';
}
